import { ActionContext, MutationTree, ActionTree, GetterTree } from 'vuex'
import { filter, each, extend, find, cloneDeep, intersection, orderBy } from 'lodash'
import webApi from '../webApi'
import { ExportProof, ExportProofColumn, ExportProofListItem, Customer, Country } from '../webApi/DataModels'
import constants from '../constants'

interface IExportProofListFilter {
  term: string;
  customers: number[];
  countries: number[];
}

class ExportProofState {
  public exportProofColumns:ExportProofColumn[] = []
  public exportProofList: ExportProofListItem[] = []
  public filteredExportProofList: ExportProofListItem[] = []
  public exportProofListFilter: IExportProofListFilter = { term: '', customers: [], countries: [] }
  public loadedExportProof: ExportProof = null
  public currentEditExportProof: ExportProof = null
  public exportProofResult: any = null
}

function resolveValues (ids: number[], values: any[], idFieldName: string, valueFieldName: string, resolvedLabel: string): String {
  var resolvedValues = []
  each(ids, (id) => {
    var filterObject = {}
    filterObject[idFieldName] = id
    var value = find(values, filterObject)
    if (value && value[valueFieldName]) {
      resolvedValues.push(value[valueFieldName])
    }
  })
  if (resolvedValues.length > 3) {
    return resolvedValues.length + ' ' + resolvedLabel
  } else if (resolvedValues.length === 0) {
    return ''
  } else {
    return resolvedValues.join(', ')
  }
}

const mutations = <MutationTree<ExportProofState>> {
  // sets the exportProof list and resolves the country and customer names
  setExportProofList (state: ExportProofState, { list, customers, countries }: { list: ExportProofListItem[], customers: Customer[], countries: Country[] }) {
    each(list, (exportProofItem: ExportProofListItem, index: number) => {
      extend(exportProofItem, {
        Country_Name: resolveValues(exportProofItem.Export_Proof_Filter.Countries, countries, 'Country_ID', 'Country_Name', 'Countries')
      })

      extend(exportProofItem, {
        Customer_Name: resolveValues(exportProofItem.Export_Proof_Filter.Customers, customers, 'Customer_ID', 'Customer_Name', 'Customers')
      })
    })

    state.exportProofList = list
    state.filteredExportProofList = list
  },
  setLoadedExportProof (state: ExportProofState, exportProof: ExportProof): void {
    state.loadedExportProof = exportProof
  },
  setExportProofResult (state: ExportProofState, result: any[]): void {
    state.exportProofResult = result
  },
  resetExportProof (state: ExportProofState): void {
    state.loadedExportProof = null
  },
  resetExportProofResult (state: ExportProofState): void {
    state.exportProofResult = null
  },
  setExportProofColumns (state: ExportProofState, columns: ExportProofColumn[]): void {
    state.exportProofColumns = columns
  },
  // Sets the filter for the exportProof list
  setFilter (state: ExportProofState, exportProofListFilter: IExportProofListFilter): void {
    state.exportProofListFilter = exportProofListFilter
    state.filteredExportProofList = filter(state.exportProofList, (exportProof) => {
      if (exportProofListFilter.term.length === 0 && exportProofListFilter.customers.length === 0 && exportProofListFilter.countries.length === 0) {
        return true
      }

      var addExportProof = false
      if (exportProofListFilter.term.length > 0 && (exportProof.User_Name.toLowerCase().indexOf(exportProofListFilter.term.toLowerCase()) !== -1 ||
        exportProof.Export_Proof_Name.toLowerCase().indexOf(exportProofListFilter.term.toLowerCase()) !== -1)) {
        addExportProof = true
      } else if (intersection(exportProof.Export_Proof_Filter.Customers, exportProofListFilter.customers).length > 0) {
        addExportProof = true
      } else if (intersection(exportProof.Export_Proof_Filter.Countries, exportProofListFilter.countries).length > 0) {
        addExportProof = true
      }
      return addExportProof
    })
  },
  filteredExportProofList (state:ExportProofState, { sortFields, sortDirections }) {
    state.filteredExportProofList = orderBy(state.filteredExportProofList, sortFields, sortDirections)
    state.exportProofList = orderBy(state.exportProofList, sortFields, sortDirections)
  },
  setCurrentEditExportProof (state: ExportProofState, exportProof: ExportProof): void {
    state.currentEditExportProof = exportProof
  },
  reset (state:ExportProofState) {
    state.exportProofColumns = []
    state.exportProofList = []
    state.filteredExportProofList = []
    state.exportProofListFilter = { term: '', customers: [], countries: [] }
    state.loadedExportProof = null
    state.currentEditExportProof = null
    state.exportProofResult = null
  }
}

const actions = <ActionTree<ExportProofState, any>>{
  reset (store: ActionContext<ExportProofState, any>):void {
    store.commit('reset')
  },
  loadExportProofColumnSetup (store: ActionContext<ExportProofState, any>): Promise<any> {
    return store.dispatch('user/hasPermission', { permissionType: constants.PERMISSION_TYPES.EXPORT_PROOF, permissionValue: constants.PERMISSION_VALUES.LIST }, { root: true })
      .then((hasPermission):any => {
        if (!hasPermission) {
          return Promise.resolve('not needed')
        } else {
          return webApi.exportProof.get_Export_Proof_Column_Setup().then(
            (response) => {
              store.commit('setExportProofColumns', response.data)
              return Promise.resolve('success')
            }).catch((response) => {
            window.bus.$emit('openErrorModal', response.data.ExceptionMessage)
            return Promise.reject(response)
          })
        }
      })
  },
  // loads the exportProof list
  loadExportProofList (store: ActionContext<ExportProofState, any>): Promise<any> {
    return webApi.exportProof.get_Export_Proof_Template_List().then(
      (response) => {
        store.commit('setExportProofList', {
          list: response.data,
          customers: store.rootState.customer.customers,
          countries: store.rootState.country.countries
        })
        return Promise.resolve('success')
      }).catch((response) => {
      window.bus.$emit('openErrorModal', response.data.ExceptionMessage)
      return Promise.reject(response)
    })
  },
  // loads a exportProof
  loadExportProof (store: ActionContext<ExportProofState, any>, exportProofID: number): Promise<any> {
    store.commit('resetExportProof')
    return webApi.exportProof.get_Export_Proof_Template(exportProofID).then(
      (response: any) => {
        var result: ExportProof = response.data as ExportProof
        store.commit('setLoadedExportProof', result)
        return Promise.resolve('success')
      }).catch((response) => {
      window.bus.$emit('openErrorModal', response.data.ExceptionMessage)
      return Promise.reject(response)
    })
  },
  // saves the exportProof to the database
  saveExportProof (store: ActionContext<ExportProofState, any>, exportProof: ExportProof): Promise<any> {
    var exportProofTransport = cloneDeep(exportProof)
    exportProofTransport.To_Mail_List = exportProofTransport.Export_Proof_Filter.To_Mail_List
    delete exportProofTransport.Export_Proof_Filter.From_Date
    delete exportProofTransport.Export_Proof_Filter.To_Date
    return webApi.exportProof.save_Export_Proof_Template(exportProofTransport).then(
      (response) => {
        return Promise.resolve(response)
      }).catch((response) => {
      window.bus.$emit('openErrorModal', response.data.ExceptionMessage)
      return Promise.reject(response)
    })
  },
  // executes the exportProof (layout transformation is done when loading the exportProof)
  executeExportProof (store: ActionContext<ExportProofState, any>, exportProof: ExportProof): Promise<any> {
    store.commit('resetExportProofResult')
    var exportProofLayout = {
      Filter: exportProof.Export_Proof_Filter,
      Columns: exportProof.Export_Proof_Layout
    }
    return webApi.exportProof.execute_Export_Proof(exportProofLayout).then(
      (response) => {
        store.commit('setExportProofResult', response.data)
        return Promise.resolve('success')
      }).catch((error) => {
      window.bus.$emit('openErrorModal', error.response.data)
      return Promise.reject(error)
    })
  },
  setFilter ({ commit }, filter: IExportProofListFilter) {
    commit('setFilter', filter)
  },
  sortList ({ commit }, { sortFields, sortDirections }): void {
    commit('filteredExportProofList', { sortFields, sortDirections })
  }
}

const getters = <GetterTree<ExportProofState, any>>{
  exportProofList: (state): ExportProofListItem[] => {
    return state.filteredExportProofList
  },
  loadedExportProof: (state): ExportProof => {
    return state.loadedExportProof
  },
  exportProofResult: (state): any => {
    return state.exportProofResult
  }
}

export default ({
  namespaced: true,
  state: new ExportProofState(),
  mutations,
  actions,
  getters
})
